import { FormattedMessage } from 'react-intl'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import {
    ListCreatorCardsApiArg,
    useListCreatorCardsBundleQuery,
    useListCreatorCardsQuery,
} from 'store/api-slice/hooks'
import { useApp } from 'store/steps'
import Loader from 'components/shared/Loader'
import { useEffect, useMemo } from 'react'
import { useAutoScroll } from 'utils/hooks'
import DocsIcon from 'assets/images/unlimitedCard/docs.svg'
import DocsIconDarkMode from 'assets/images/unlimitedCard/docs-mode_dark.svg'
import { BundledProductsData, CardPerson } from 'store/api-slice/types'
import { CARD_REGULATIONS_LINK } from 'constants/links'
import { CardsWrapper } from 'components/shared/UnlimitedCard/styles'
import { DefaultTheme } from 'styled-components'
import VoucherUnlimitedCard from './CardVoucherConfiguration/VoucherUnlimitedCard'
import { useAppSelector } from 'store/hooks'
import { calculateProductTypeForVoucherBundleItem } from 'helpers/bundles'

interface VoucherCardTypePickerProps {
    index: number
    personType: CardPerson
    theme?: DefaultTheme
}

const VoucherCardTypePicker = ({
    index,
    personType,
    theme,
}: VoucherCardTypePickerProps) => {
    const { language } = useApp()
    const { animatePendingScroll, scrollIndex } = useAutoScroll()

    const { cardVouchersProducts } = useAppSelector((state) => state.vouchers)

    const queriesParams: ListCreatorCardsApiArg = {
        _locale: language,
        person: personType,
        cartCreatorType: 'card',
    }
    const queriesConfig = {
        skip: !personType,
    }

    const { data: cardsData, isFetching: isCardsFetching } =
        useListCreatorCardsQuery(queriesParams, queriesConfig)
    const { data: bundlesData, isFetching: isBundlesFetching } =
        useListCreatorCardsBundleQuery(queriesParams, queriesConfig)

    const isDataFetching = isCardsFetching || isBundlesFetching
    const hasDarkMode = theme?.mode === 'dark'

    // TODO: try to unify listing cards between vouchers and unlimited cards
    // - combine it into one, universal, cards list component

    const cardsToDisplay = useMemo(() => {
        const cards = cardsData?.cards || []
        const cardsRefactored = cards.map((card) => {
            const matchingVoucher = cardVouchersProducts.find(
                (voucher) =>
                    voucher.voucheredProductId === card.product?.productId
            )
            const bundleItemsData: BundledProductsData = []

            matchingVoucher?.items?.forEach(
                ({ productId, productType, name }) => {
                    if (productId && productType)
                        bundleItemsData.push({
                            productId,
                            productType:
                                calculateProductTypeForVoucherBundleItem(name),
                        })
                }
            )

            return {
                ...card,
                bundleData: [...bundleItemsData],
                product: {
                    ...card.product,
                    productId: matchingVoucher?.productId,
                },
            }
        })
        const bundledCards = bundlesData?.cards || []
        const bundledCardsRefactored = bundledCards.map((bundle) => {
            const { items, unitPrice } = bundle
            const bundledCard = items?.find(
                ({ productType }) => productType === 'card'
            )
            const { detail, name, offers, zones } = bundledCard || {}
            const matchingVoucher = cardVouchersProducts.find(
                (voucher) =>
                    voucher.voucheredProductId === bundledCard?.productId
            )
            const bundleItemsData: BundledProductsData = []

            matchingVoucher?.items?.forEach(
                ({ productId, productType, name }) => {
                    if (productId && productType)
                        bundleItemsData.push({
                            productId,
                            productType:
                                calculateProductTypeForVoucherBundleItem(name),
                        })
                }
            )

            return {
                product: {
                    productId: matchingVoucher?.productId,
                    productType: 'card',
                    name,
                    unitPrice,
                },
                detail,
                zones,
                offers,
                bundleData: [...bundleItemsData],
            }
        })
        const combinedCards = [...cardsRefactored, ...bundledCardsRefactored]
        const unlimitedCardPlusData = combinedCards.find((item) =>
            item.detail?.cardType?.value?.includes('unlimited_plus')
        )

        return combinedCards.map(
            (item, itemIndex) =>
                unlimitedCardPlusData && (
                    <VoucherUnlimitedCard
                        key={`${item.product?.productId}${itemIndex}`}
                        index={index}
                        cardSpecifications={item}
                        unlimitedPlusData={unlimitedCardPlusData}
                        theme={theme}
                    />
                )
        )
    }, [cardsData, bundlesData, index])

    useEffect(() => {
        if (!isDataFetching) animatePendingScroll(`card_type-${index}`)
    }, [isDataFetching, scrollIndex])

    return (
        <Box
            isFullWidth
            justify="space-between"
            align="stretch"
            id={`card_type-${index}`}
            pt={8}
            pb={[0, 16]}
            mt={[24, 56]}
        >
            <Text variant="heading3" mb={[16, 24]} theme={theme}>
                <FormattedMessage
                    id="annual.config.section_card_type_caption"
                    defaultMessage="Wybierz rodzaj karty"
                />
            </Text>

            <Box direction="column" gap={16}>
                {isDataFetching && <Loader />}

                {!isDataFetching && cardsToDisplay && (
                    <>
                        <CardsWrapper gap={[16, 0]} isFullWidth>
                            {cardsToDisplay}
                        </CardsWrapper>
                        <Box direction="row" align="center" gap={[10, 12]}>
                            <img
                                src={hasDarkMode ? DocsIconDarkMode : DocsIcon}
                                alt=""
                            />
                            <Text
                                as="a"
                                href={CARD_REGULATIONS_LINK}
                                target="_blank"
                                variant="bodyStrong"
                                color="secondary_500"
                                theme={theme}
                                data-testid={`voucher-${index}-redirect-unlimited-regulations`}
                            >
                                <FormattedMessage
                                    id="annual.cards_terms_link"
                                    defaultMessage="Regulamin kart całorocznych"
                                />
                            </Text>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    )
}

export default VoucherCardTypePicker
